import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_CTA, NAV_LINKS } from "../../data/nav";
/*
  Dos versiones del wordmark, una por superficie.

  El header es transparente sobre el hero oscuro y pasa a papel al hacer
  scroll, así que un solo archivo no puede servir a los dos estados:

  - Sobre papel va el logotipo de marca, a dos tonos (navy + arena). Es el
    que corresponde y el que el cliente usa en el resto de sus piezas.
  - Sobre el hero oscuro va la versión monocroma en arena. La mitad navy del
    logotipo de marca es #0A1631: sobre el carbón #141210 del hero se pierde,
    y media marca invisible es peor que la marca en un solo tono.
*/
import logoClaro from "../../assets/logo-omega.png";
import logoColor from "../../assets/logo-omega-color.png";

const EASE = [0.16, 1, 0.3, 1];

const SiteHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const linkClass = solid
    ? "text-sm font-medium text-navy-600 transition-colors hover:text-navy-900"
    : "text-sm font-medium text-navy-200 transition-colors hover:text-white";

  return (
    <>
      <header
        className={[
          "fixed inset-x-0 top-0 z-50 transition-all duration-500 ease-omega",
          solid
            ? "border-b border-navy-100 bg-sand-50/90 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent",
        ].join(" ")}
      >
        <div className="container-omega">
          <div className="flex h-[4.5rem] items-center justify-between gap-8 lg:h-20">
            <Link to="/" aria-label="Omega Digital, ir al inicio" className="flex shrink-0 items-center">
              <img
                src={solid ? logoColor : logoClaro}
                alt="Omega Digital"
                width="145"
                height="36"
                className="h-9 w-auto lg:h-10"
              />
            </Link>

            <nav aria-label="Principal" className="hidden lg:block">
              <ul className="flex items-center gap-8">
                {NAV_LINKS.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className={linkClass}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="flex items-center gap-3">
              <Link to={NAV_CTA.to} className={solid ? "btn-omega-sm hidden sm:inline-flex" : "btn-on-dark hidden !px-5 !py-2.5 sm:inline-flex"}>
                {NAV_CTA.label}
              </Link>
              <button
                type="button"
                onClick={() => setMenuOpen(true)}
                aria-label="Abrir menú"
                aria-expanded={menuOpen}
                className={`flex h-10 w-10 items-center justify-center rounded-full border lg:hidden ${
                  solid ? "border-navy-200 text-navy-900" : "border-white/15 text-white"
                }`}
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen ? (
          <motion.div
            className="fixed inset-0 z-[60] bg-sand-50 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="container-omega flex h-[4.5rem] items-center justify-between">
              <img src={logoColor} alt="Omega Digital" width="145" height="36" className="h-9 w-auto" />
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="Cerrar menú"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-navy-200 text-navy-900"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <nav aria-label="Menú móvil" className="container-omega mt-10">
              <ul>
                {NAV_LINKS.map((link, index) => (
                  <motion.li
                    key={link.to}
                    className="border-b border-navy-100"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.06 + index * 0.05, ease: EASE }}
                  >
                    <Link
                      to={link.to}
                      onClick={() => setMenuOpen(false)}
                      className="block w-full py-5 text-left font-display text-2xl font-bold tracking-tight text-navy-900"
                    >
                      {link.label}
                    </Link>
                  </motion.li>
                ))}
              </ul>
              <Link to={NAV_CTA.to} className="btn-omega mt-10 w-full" onClick={() => setMenuOpen(false)}>
                {NAV_CTA.label}
              </Link>
            </nav>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default SiteHeader;
